<template>
  <div
    v-if="!isOnline"
    class="flex sticky top-0 z-50 w-full bg-gray-200 place-content-center"
  >
    <p class="m-2">You are currently in offline mode</p>
  </div>
  <div
    v-if="isSmsOptedOut"
    class="flex sticky top-0 z-50 w-full bg-red-500 place-content-center text-center"
  >
    <p class="m-2">
      You are subscribed to SMS Alerts but your number is opted out! Please go to
      'Account' and verify your emergency contact info.
    </p>
  </div>
  <div
    v-if="displayUnverifiedBanner()"
    class="flex sticky top-0 z-50 w-full bg-red-500 place-content-center text-center"
  >
    <p class="m-2">
      Your email has not been verified. Please check your email for verification link to
      access secure site content.
    </p>
  </div>
  <header>
    <!-- HEADER DESKTOP -->
    <div
      class="skyline-bg block text-white pl-6 md:pl-12 pr-2 sm:pr-6 md:pr-12 pt-10 pb-6 2xl:px-64 bg-no-repeat"
    >
      <div class="container mx-auto">
        <div class="flex">
          <div class="flex-1 grow">
            <router-link :to="isPropertyLoading ? '' : `/${bldgStore?.siteCode}`">
              <img
                :src="safetyNetLogoPath"
                alt="Site Logo"
                class="sm:max-h-16 max-h-10"
              />
            </router-link>
            <span
              class="block mt-1 text-lg sm:text-2xl"
              v-if="property?.isActive && property.name"
            >
              <div>
                <span
                  :class="{
                    'pr-1 cursor-pointer border-b-2 border-white hover:text-blue-300 hover:border-blue-300': enableBuildingSwitcher,
                  }"
                  @click="enableBuildingSwitcher ? emit('openBuildingSwitcher') : {}"
                >
                  <IHeroiconsOutlineChevronDown
                    v-if="enableBuildingSwitcher"
                    class="inline"
                  />
                  {{ property?.name }}
                </span>
              </div>
              <div v-if="isLoggedIn" class="text-lg mt-2 cursor-auto">
                {{ user?.firstName ?? "" }}
                {{ user?.lastName ?? "" }}
              </div>
            </span>
          </div>
          <div v-if="property?.isActive && !isPropertyLoading" class="w-auto">
            <div v-if="customLogoPath">
              <div class="text-newlg mb-4">
                <img :src="customLogoPath" alt="Site Logo" class="max-h-24" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-lightblue text-white h-8 px-5 lg:px-12 2xl:px-64">
      <div class="container mx-auto">
        <!-- px-6 md:px-12 2xl:px-64 -->
        <div v-if="property?.isActive" class="text-nav flex lg:pr-0">
          <div class="flex grow"></div>
          <div class="md:block hidden px-6 hover:text-blue-100">
            <router-link :to="isPropertyLoading ? '' : `/${bldgStore?.siteCode}`">
              Home
            </router-link>
          </div>
          <div class="md:flex-row flex-col hover:text-blue-100" v-if="isOnline">
            <router-link v-if="isLoggedIn" :to="'/' + property?.siteCode + '/account'">
              Account
            </router-link>
            <router-link
              v-else
              data-cy="LoginPageLink"
              :to="'/' + property?.siteCode + '/login'"
            >
              Login
            </router-link>
          </div>
          <div class="pl-4 hover:text-blue-100" v-if="isAdmin && isOnline">
            <router-link :to="'/' + property?.siteCode + '/admin'"> Admin </router-link>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { watch, ref, computed, onMounted } from "vue";
import { useBldgStore } from "../store/bldgStore";
import { useUserStore } from "../store/userStore";
import { useUtilityStore } from "../store/utilityStore";

const contentApiPath = "/api/property/content/file";
const safetyNetLogoPath = "/images/safetyNetLogo.png";
var customLogoPath = ref("");
var bldgStore = useBldgStore();
var userStore = useUserStore();
const utilityStore = useUtilityStore();
var isPropertyLoading = computed(() => bldgStore.isLoading);
var property = computed(() => bldgStore.property);
var isLoggedIn = computed(() => userStore.isLoggedIn);
var user = computed(() => userStore.user);
var isAdmin = computed(() => userStore.isAdmin);

utilityStore.setupOfflineListeners();
var isOnline = computed(() => utilityStore.isOnline);

var isSmsOptedOut = computed(() =>
  user.value
    ? user.value.contactSms &&
      user.value.contactSms.length > 0 &&
      user.value.isSmsOptedOut
    : false
);

const props = defineProps<{
  title?: string;
  enableBuildingSwitcher?: boolean;
}>();

// Sets document.title. A <title> element used to sit in this component's template,
// but a <title> outside <head> is inert, so tabs showed the raw URL instead.
useTitle(computed(() => (props.title ? `${props.title} - SafetyNet` : "SafetyNet")));

const emit = defineEmits<{
  (e: "openBuildingSwitcher"): void;
}>();

onMounted(loadData);
watch(property, loadData);
async function loadData() {
  //console.log("Loading data...");
  if (property?.value?.portalId) {
    let customLogo = property.value.content?.find((c) => c.contentType === 1);
    if (customLogo)
      customLogoPath.value = `${contentApiPath}/${property.value.siteCode}/${customLogo.contentId}`;
  }
}

function displayUnverifiedBanner() {
  // Check if the user is logged in and the property is active and the tenantId is available
  // If the user is logged in and the property is active and the tenantId is available
  // Check if the tenant requires email verification
  //console.log("Checking for unverified banner...");
  if (isLoggedIn.value && property.value && user?.value?.tenantId) {
    let tenants = property.value?.tenants;
    let thisUsersTenant = tenants?.filter((t) => t?.tenantId == user.value?.tenantId)[0];

    // If this tenant requires email verification and the user is not verified
    if (thisUsersTenant?.isEmailVerificationRequired ?? false) {
      return !user.value?.isEmailVerified || false;
    }
  }
  return false;
}
</script>

<style>
.header_graphic {
  background-image: url("/images/bg_hdr_tall_wide.png"),
    linear-gradient(
      90.81deg,
      rgba(5, 58, 108, 0.8) 0.95%,
      rgba(0, 49, 97, 0.8) 18.75%,
      rgba(1, 31, 60, 0.8) 53.5%
    );
  background-color: #03366a;
}

.skyline-bg {
  background-color: #03366a;
  background: linear-gradient(
      90.81deg,
      rgba(5, 58, 108, 0.8) 0.95%,
      rgba(0, 49, 97, 0.8) 18.75%,
      rgba(1, 31, 60, 0.8) 53.5%
    ),
    url("/images/skyline.jpg");

  background-position: 50% 50%;
  background-size: cover;
}
</style>
